import type { UseQueryReturnType } from '@tanstack/vue-query';
import type {
  Column,
  ColumnFiltersState,
  PaginationState,
  SortingState,
  TableOptions,
  Table as TanstackTable,
  VisibilityState,
} from '@tanstack/vue-table';
import {
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  useVueTable,
} from '@tanstack/vue-table';
import { ref, toValue, watch } from 'vue';
import type { MaybeRef } from 'vue';
import { useRouter } from 'vue-router';

import { valueUpdater } from '@ui/utils/utils';

interface TablePaginatedQueryParams {
  paginationState: PaginationState;
  serialize: () => Record<string, any>;
  sortState: SortingState;
  state: { pagination?: PaginationState; sort?: SortingState };
}

export interface UseTableOptions<T, Q extends TablePaginatedQueryParams> {
  onQueryChange?: () => any;
  params: MaybeRef<Q>;
  query: UseQueryReturnType<T[], any>;
  tableOptions: Omit<
    TableOptions<T>,
    'data' | 'getCoreRowModel' | 'manualFiltering' | 'manualPagination' | 'manualSorting' | 'state'
  >;
  useRouterQuery?: boolean;
}

export interface UseTableReturnType<T> {
  table: TanstackTable<T>;
}

export function usePaginatedTable<T, Q extends TablePaginatedQueryParams>(
  options: UseTableOptions<T, Q>,
): UseTableReturnType<T> {
  const router = useRouter();
  // const route = useRoute();
  const table = ref<TanstackTable<T>>();

  const query = ref(toValue(options.query));
  const queryParams = ref<Q>(toValue(options.params));

  const pagination = ref<PaginationState>(queryParams.value.paginationState);
  const sorting = ref<SortingState>(queryParams.value.sortState);
  const columnFilters = ref<ColumnFiltersState>([]);
  const columnVisibility = ref<VisibilityState>({});
  const rowSelection = ref({});

  const isInternalChange = ref<any>(null);

  watch(pagination, () => {
    queryParams.value.state = {
      pagination: pagination.value,
      sort: sorting.value,
    };
  });

  watch(
    queryParams,
    (params: TablePaginatedQueryParams) => {
      if (options.useRouterQuery && isInternalChange.value !== false) {
        router.push({ query: params.serialize() });
      } else {
        isInternalChange.value = null;
      }
      options.onQueryChange && options.onQueryChange();
    },
    { deep: true },
  );

  const createTable = (): TanstackTable<T> => {
    return useVueTable<T>({
      get data() {
        return query.value.data || [];
      },
      getCoreRowModel: getCoreRowModel(),
      getFacetedRowModel: getFacetedRowModel(),
      getFacetedUniqueValues: getFacetedUniqueValues(),
      getFilteredRowModel: getFilteredRowModel(),
      getSortedRowModel: getSortedRowModel(),
      manualPagination: true,
      manualSorting: true,
      onColumnFiltersChange: (updaterOrValue) => valueUpdater(updaterOrValue, columnFilters),
      onColumnVisibilityChange: (updaterOrValue) => valueUpdater(updaterOrValue, columnVisibility),
      onPaginationChange: (updatedOrValue) => {
        valueUpdater(updatedOrValue, pagination);
      },
      onRowSelectionChange: (updaterOrValue) => valueUpdater(updaterOrValue, rowSelection),
      onSortingChange: (updaterOrValue) => {
        valueUpdater(updaterOrValue, sorting);
        pagination.value = {
          pageIndex: 0,
          pageSize: pagination.value.pageSize,
        };
      },
      state: {
        get columnFilters() {
          return columnFilters.value;
        },
        get columnVisibility() {
          return columnVisibility.value;
        },
        get pagination() {
          return pagination.value;
        },
        get rowSelection() {
          return rowSelection.value;
        },
        get sorting() {
          return sorting.value;
        },
      },
      ...options.tableOptions,
    });
  };

  table.value = createTable();

  return {
    table: toValue(table) as TanstackTable<T>,
  };
}

export const getColumnSize = (column: Column<any>, layout: 'fixed' | 'auto'): string => {
  const size = column.getSize();
  const columnDef = column.columnDef;
  if (size === Number.MAX_SAFE_INTEGER || layout === 'auto') {
    const parts: string[] = [];
    if (layout === 'auto' || (columnDef.minSize && columnDef.minSize !== Number.MAX_SAFE_INTEGER)) {
      parts.push(
        `min-width: ${columnDef.minSize === Number.MAX_SAFE_INTEGER ? columnDef.size : columnDef.minSize}px`,
      );
    }

    if (layout === 'auto' || (columnDef.maxSize && columnDef.maxSize !== Number.MAX_SAFE_INTEGER)) {
      parts.push(
        `max-width: ${columnDef.maxSize === Number.MAX_SAFE_INTEGER ? columnDef.size : columnDef.maxSize}px`,
      );
    }

    if (!parts.length) {
      parts.push('width: auto');
    } else {
      parts.push(`width: 100%`);
    }

    return parts.join('; ');
  }

  return `width: ${size}px;`;
};
